// src/config.js
/**
 * Default configuration for Verified User Tracking
 */
export const DEFAULT_CONFIG = {
  // The UTM parameter that must exist to enable tracking
  requiredUtmParam: "utm_tracker",
  requiredUtmValue: "trackxact",

  // Engagement thresholds
  minTimeOnPageSeconds: 4,
  minScrollDepthPercent: 5,

  // Server endpoint (sGTM)
  endpoint: "https://sst.trackxact.com/data",

  // Event name
  eventName: "VerifiedUser",

  // Storage keys
  storageKeys: {
    params: "_vt_params",
    eventSent: "_vt_sent",
    consentGiven: "_vt_consent",
  },

  // Debug settings
  debug: false,
  debugShowPanel: false,
  externalLogsEnabled: true,

  thresholdsCombiningLogic: true, // true = AND, false = OR

  enableUnloadTracking: true,
  unloadEventName: "VerifiedUser_Unload",
};

/**
 * Merge custom config with defaults
 * @param {Object} customConfig - Custom configuration options
 * @returns {Object} - Merged configuration
 */
export function createConfig(customConfig = {}) {
  const configValidation = validateCustomConfig(customConfig, DEFAULT_CONFIG);
  const validatedConfig = configValidation.config;

  return {
    ...DEFAULT_CONFIG,
    ...validatedConfig,
    storageKeys: {
      ...DEFAULT_CONFIG.storageKeys,
      ...(customConfig?.storageKeys || {}),
    },
    warnings: !configValidation.isValid ? configValidation.warnings : null,
  };
}

/**
 * Validates and sanitizes the customConfig object using default values for invalid fields
 * @param {Object} config - The configuration object to validate
 * @param {Object} defaultConfig - The default configuration to fall back to
 * @returns {Object} - Validation result with sanitized config, warnings, and validity status
 */
function validateCustomConfig(config, defaultConfig) {
  const warnings = [];
  const sanitizedConfig = {};

  // Check if config exists and is an object
  if (!config || typeof config !== "object") {
    return {
      isValid: false,
      config: defaultConfig,
      warnings: [
        "Config must be a valid object - using full default configuration",
      ],
    };
  }

  // Check if defaultConfig exists
  if (!defaultConfig || typeof defaultConfig !== "object") {
    return {
      isValid: false,
      config: config,
      warnings: ["Default configuration is missing or invalid"],
    };
  }

  // Validate and sanitize string fields (only if they exist in config)
  const stringFields = [
    "requiredUtmParam",
    "requiredUtmValue",
    "endpoint",
    "eventName",
  ];

  stringFields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(config, field)) {
      if (
        !config[field] ||
        typeof config[field] !== "string" ||
        config[field].trim() === ""
      ) {
        sanitizedConfig[field] = defaultConfig[field];
        warnings.push(
          `${field} is invalid - using default value: "${defaultConfig[field]}"`
        );
      } else {
        sanitizedConfig[field] = config[field];
      }
    }
  });

  // Special validation for endpoint URL format (only if endpoint exists)
  if (Object.prototype.hasOwnProperty.call(sanitizedConfig, "endpoint")) {
    try {
      new URL(sanitizedConfig.endpoint);
    } catch (e) {
      sanitizedConfig.endpoint = defaultConfig.endpoint;
      warnings.push(
        `endpoint is not a valid URL - using default: "${defaultConfig.endpoint}"`
      );
    }
  }

  // Validate and sanitize number fields (only if they exist in config)
  if (Object.prototype.hasOwnProperty.call(config, "minTimeOnPageSeconds")) {
    if (
      typeof config.minTimeOnPageSeconds !== "number" ||
      config.minTimeOnPageSeconds < 0
    ) {
      sanitizedConfig.minTimeOnPageSeconds = defaultConfig.minTimeOnPageSeconds;
      warnings.push(
        `minTimeOnPageSeconds is invalid - using default: ${defaultConfig.minTimeOnPageSeconds}`
      );
    } else {
      sanitizedConfig.minTimeOnPageSeconds = config.minTimeOnPageSeconds;
    }
  }

  if (Object.prototype.hasOwnProperty.call(config, "minScrollDepthPercent")) {
    if (
      typeof config.minScrollDepthPercent !== "number" ||
      config.minScrollDepthPercent < 0 ||
      config.minScrollDepthPercent > 100
    ) {
      sanitizedConfig.minScrollDepthPercent =
        defaultConfig.minScrollDepthPercent;
      warnings.push(
        `minScrollDepthPercent is invalid - using default: ${defaultConfig.minScrollDepthPercent}`
      );
    } else {
      sanitizedConfig.minScrollDepthPercent = config.minScrollDepthPercent;
    }
  }

  // Validate and sanitize storageKeys object (only if it exists in config)
  if (Object.prototype.hasOwnProperty.call(config, "storageKeys")) {
    if (!config.storageKeys || typeof config.storageKeys !== "object") {
      sanitizedConfig.storageKeys = { ...defaultConfig.storageKeys };
      warnings.push("storageKeys is invalid - using default values");
    } else {
      sanitizedConfig.storageKeys = {};
      const requiredStorageKeys = ["params", "eventSent", "consentGiven"];
      requiredStorageKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(config.storageKeys, key)) {
          if (
            !config.storageKeys[key] ||
            typeof config.storageKeys[key] !== "string" ||
            config.storageKeys[key].trim() === ""
          ) {
            sanitizedConfig.storageKeys[key] = defaultConfig.storageKeys[key];
            warnings.push(
              `storageKeys.${key} is invalid - using default: "${defaultConfig.storageKeys[key]}"`
            );
          } else {
            sanitizedConfig.storageKeys[key] = config.storageKeys[key];
          }
        }
      });
    }
  }

  // Validate and sanitize boolean fields (only if they exist in config)
  const booleanFields = ["debug", "debugShowPanel", "thresholdsCombiningLogic"];
  booleanFields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(config, field)) {
      if (typeof config[field] === "boolean") {
        sanitizedConfig[field] = config[field];
      } else {
        sanitizedConfig[field] = defaultConfig[field];
        warnings.push(
          `${field} is invalid - using default: ${defaultConfig[field]}`
        );
      }
    }
  });

  return {
    isValid: warnings.length === 0,
    config: sanitizedConfig,
    warnings: warnings,
  };
}
